import type { ILandType, ILatLon, ITax } from '@curbnturf/entities';
import { PERSONA_CATEGORY_META } from './category-meta';
import { DUMP_STATION_ICON } from '../icons/custom-icons';
import type { IDraftListing, IHouseRules } from './draft-listing.types';
import { IPublishedSnapshot, readPublishedSnapshot } from './published-snapshot.util';

/**
 * `'vineyard'`/`'offgrid'` are this file's original 5-bucket marketing taxonomy,
 * kept as aliases of `'winery'`/`'nature'` for backward compatibility with
 * existing mock data + host-wizard code (see `category-meta.ts`'s doc comment —
 * that file's `PersonaCategory` is the canonical 6-bucket taxonomy from
 * "CurbNTurf category taxonomy — unify host / data / server"; renaming every
 * `'vineyard'`/`'offgrid'` call site to match it is deferred/out of scope here).
 * `'winery'`/`'driveway'`/`'nature'` are added so real backend `category` values
 * (search_listing.category, per that same doc) resolve correctly in CATEGORY_META
 * lookups without needing a lossy client-side re-derivation.
 */
export type Category = 'vineyard' | 'farm' | 'brewery' | 'attraction' | 'offgrid' | 'winery' | 'driveway' | 'nature';

/**
 * Maps each marketing `Category` onto @curbnturf/objects' operational `LandType`
 * taxonomy where there's a clean, defensible match. `attraction`/`offgrid`/`nature`
 * deliberately have no entry: both are broad marketing buckets spanning many
 * actual terrain types (coastal/mountains/urban/lake/canyon for `attraction`;
 * desert/forest/mountains/wetlands for `offgrid`/`nature`) — picking a single LandType
 * for either would misrepresent real listings rather than align them. Needs a
 * content/product decision (probably a per-listing land type, not a per-category
 * default) before those two can be filled in.
 */
const CATEGORY_LAND_TYPE: Partial<Record<Category, ILandType['type']>> = {
  vineyard: 'winery',
  winery: 'winery',
  farm: 'farm',
  brewery: 'brewery',
};

export function landTypesForCategory(category: Category): ILandType['type'][] {
  const landType = CATEGORY_LAND_TYPE[category];
  return landType ? [landType] : [];
}

export const CATEGORY_META: Record<Category, { label: string; color: string; icon: string }> = {
  vineyard: { label: 'Vineyard', color: '#A541A1', icon: 'wine_bar' },
  farm: {
    label: PERSONA_CATEGORY_META.farm.label,
    color: PERSONA_CATEGORY_META.farm.color,
    icon: PERSONA_CATEGORY_META.farm.icon,
  },
  brewery: {
    label: PERSONA_CATEGORY_META.brewery.label,
    color: PERSONA_CATEGORY_META.brewery.color,
    icon: PERSONA_CATEGORY_META.brewery.icon,
  },
  attraction: {
    label: PERSONA_CATEGORY_META.attraction.label,
    color: PERSONA_CATEGORY_META.attraction.color,
    icon: PERSONA_CATEGORY_META.attraction.icon,
  },
  offgrid: { label: 'Off-Grid', color: '#295d42', icon: 'forest' },
  winery: {
    label: PERSONA_CATEGORY_META.winery.label,
    color: PERSONA_CATEGORY_META.winery.color,
    icon: PERSONA_CATEGORY_META.winery.icon,
  },
  driveway: {
    label: PERSONA_CATEGORY_META.driveway.label,
    color: PERSONA_CATEGORY_META.driveway.color,
    icon: PERSONA_CATEGORY_META.driveway.icon,
  },
  nature: {
    label: PERSONA_CATEGORY_META.nature.label,
    color: PERSONA_CATEGORY_META.nature.color,
    icon: PERSONA_CATEGORY_META.nature.icon,
  },
};

// Aligned to @curbnturf/entities' IAmenityType['type'] union — see AmenityType enum
// for the shared display labels. 'pool' maps onto the existing 'pool' value.
export type Amenity =
  | 'backIn'
  | 'pullThrough'
  | 'electricity'
  | 'water'
  | 'dump'
  | 'sewage'
  | 'cell'
  | 'wifi'
  | 'firesAllowed'
  | 'petsAllowed'
  | 'trash'
  | 'recycling'
  | 'table'
  | 'restroom'
  | 'shower'
  | 'laundry'
  | 'tents'
  | 'vehicles'
  | 'pool'
  | 'wheelchair'
  // Added in the amenities expansion:
  | 'evCharging'
  | 'fireRing'
  | 'outdoorGrill'
  | 'generatorOk'
  | 'outdoorLighting'
  | 'sunshade'
  | 'waterFill'
  | 'lockableStorage';

export const AMENITY_LABELS: Record<Amenity, string> = {
  backIn: 'Back In',
  pullThrough: 'Pull Through',
  electricity: 'Electricity',
  water: 'Potable Water',
  dump: 'Dump Station',
  sewage: 'Sewage',
  cell: 'Cell Phone Coverage',
  wifi: 'Wifi',
  firesAllowed: 'Campfires Allowed',
  petsAllowed: 'Pets Allowed',
  trash: 'Trash Provided',
  recycling: 'Recycle Provided',
  table: 'Picnic Table',
  restroom: 'Toilet Available',
  shower: 'Shower',
  laundry: 'Laundry',
  tents: 'Tents Allowed',
  vehicles: 'Vehicles Allowed',
  pool: 'Pool',
  wheelchair: 'Wheelchair Accessible',
  evCharging: 'EV Charging',
  fireRing: 'Fire Ring',
  outdoorGrill: 'Outdoor Grill',
  generatorOk: 'Generator-Friendly',
  outdoorLighting: 'Outdoor Lighting',
  sunshade: 'Sunshade',
  waterFill: 'Drinking-Water Fill',
  lockableStorage: 'Lockable Storage',
};

/**
 * One-line explanations for amenities whose label alone is ambiguous, shown in an
 * ⓘ tooltip on the host wizard's tiles AND the guest listing page — one source so
 * both sides read the same meaning. Deliberately Partial: only labels that get
 * confused (Sewage vs Dump Station, Potable Water vs Drinking-Water Fill, Back In
 * vs Pull Through) need one. Written neutrally so it reads right to either side.
 */
export const AMENITY_DESCRIPTIONS: Partial<Record<Amenity, string>> = {
  sewage: 'A sewer hookup right at the site: RVs connect their tank hose directly, with no trip to a dump station.',
  dump: 'A dump station on the property where RVs empty their tanks. Shared, not a hookup at each site.',
  water: 'Drinkable water at the site, either as a hookup at the site or a shared fill station.',
  waterFill: 'A spot to fill drinking-water jugs or bottles, separate from an RV water hookup.',
  backIn: 'The RV reverses into the site and drives out forward.',
  pullThrough: 'The RV drives in one end and out the other with no reversing. Easiest for big rigs and anyone towing.',
  generatorOk: 'Guests may run a generator at the site.',
  vehicles: 'Extra vehicles, beyond the RV and its tow vehicle, can park at the site.',
  lockableStorage: 'A lockable space at the site where guests can keep gear.',
};

/** Guest-facing display order — the hookups a guest decides on come first, so every
 *  listing lists its amenities in the same order. Must contain every `Amenity`
 *  exactly once (asserted in mock-listings.data.spec.ts). */
export const AMENITY_PRIORITY: Amenity[] = [
  'electricity',
  'water',
  'dump',
  'sewage',
  'pullThrough',
  'backIn',
  'waterFill',
  'generatorOk',
  'restroom',
  'shower',
  'wifi',
  'cell',
  'petsAllowed',
  'trash',
  'recycling',
  'laundry',
  'wheelchair',
  'firesAllowed',
  'fireRing',
  'table',
  'outdoorGrill',
  'evCharging',
  'outdoorLighting',
  'sunshade',
  'lockableStorage',
  'pool',
  'tents',
  'vehicles',
];

const AMENITY_RANK = new Map(AMENITY_PRIORITY.map((a, i) => [a, i]));

/** Returns a copy of `list` in `AMENITY_PRIORITY` order; unknown values sort last. */
export function sortAmenities(list: readonly Amenity[]): Amenity[] {
  const rank = (a: Amenity) => AMENITY_RANK.get(a) ?? AMENITY_PRIORITY.length;
  return [...list].sort((a, b) => rank(a) - rank(b));
}

const WATER_DETAIL_LABELS: Record<string, string> = {
  direct: 'Hookup at site',
  station: 'Shared fill station',
};

/** Decodes `IAmenity.options` into guest-facing sub-detail text, aggregated across every
 *  row passed (a listing's sites, or a site's one-row-per-amp electricity entries).
 *  Vocabulary matches the legacy decoder in libs/listing's amenity-display.component.ts:
 *  electricity options are the bare amp rating ('15'|'30'|'50'), water options are
 *  'direct'|'station'. Unknown/missing options yield no entry. */
export function amenityDetailsFrom(
  amenities: readonly { type?: string; options?: string }[],
): Partial<Record<Amenity, string>> {
  const details: Partial<Record<Amenity, string>> = {};

  const amps = [
    ...new Set(
      amenities
        .filter((a) => a.type === 'electricity')
        .map((a) => Number(a.options))
        .filter((n) => n === 15 || n === 30 || n === 50),
    ),
  ].sort((a, b) => a - b);
  if (amps.length > 0) details.electricity = `${amps.join(' / ')} amp`;

  const water = new Set(
    amenities
      .filter((a) => a.type === 'water' && a.options && a.options in WATER_DETAIL_LABELS)
      .map((a) => a.options as string),
  );
  if (water.size === 2) details.water = 'Hookup & shared fill station';
  else if (water.size === 1) details.water = WATER_DETAIL_LABELS[[...water][0]];

  return details;
}

export const AMENITY_GROUP: Amenity[] = [
  'pullThrough',
  'electricity',
  'water',
  'dump',
  'sewage',
  'cell',
  'wifi',
  'firesAllowed',
  'petsAllowed',
  'trash',
  'recycling',
  'table',
  'restroom',
  'shower',
  'laundry',
  'tents',
  'vehicles',
  'pool',
  'wheelchair',
];

export const AMENITY_ICONS: Record<Amenity, string> = {
  backIn: 'directions_car',
  pullThrough: 'swap_horiz',
  electricity: 'bolt',
  water: 'water_drop',
  dump: DUMP_STATION_ICON,
  sewage: 'plumbing',
  cell: 'signal_cellular_alt',
  wifi: 'wifi',
  firesAllowed: 'local_fire_department',
  petsAllowed: 'pets',
  trash: 'delete_outline',
  recycling: 'recycling',
  table: 'outdoor_grill',
  restroom: 'wc',
  shower: 'shower',
  laundry: 'local_laundry_service',
  tents: 'camping',
  vehicles: 'commute',
  pool: 'hot_tub',
  wheelchair: 'accessible',
  evCharging: 'ev_station',
  fireRing: 'local_fire_department',
  outdoorGrill: 'outdoor_grill',
  generatorOk: 'power',
  outdoorLighting: 'wb_incandescent',
  sunshade: 'umbrella',
  waterFill: 'local_drink',
  lockableStorage: 'lock',
};

// Aligned to @curbnturf/entities' RvTypes enum keys — IRV.type is a loose string,
// but consumers should store one of these values for platform-wide consistency.
export type RvType =
  | 'classA'
  | 'classB'
  | 'classC'
  | 'fifthWheel'
  | 'travelTrailer'
  | 'truckCamper'
  | 'teardrop'
  | 'popup';

export const RV_TYPES: { id: RvType; label: string; image: string }[] = [
  { id: 'classA', label: 'Class A', image: 'assets/images/Class-A_CNT.svg' },
  { id: 'classB', label: 'Class B', image: 'assets/images/Class-B_CNT.svg' },
  { id: 'classC', label: 'Class C', image: 'assets/images/Class-C_CNT.svg' },
  { id: 'fifthWheel', label: 'Fifth Wheel', image: 'assets/images/FifthWheel_CNT.svg' },
  { id: 'travelTrailer', label: 'Travel Trailer', image: 'assets/images/Travel_CNT.svg' },
  { id: 'truckCamper', label: 'Truck Camper', image: 'assets/images/App_TruckCamper_CNT.svg' },
  { id: 'teardrop', label: 'Teardrop', image: 'assets/images/TearDrop_CNT.svg' },
  { id: 'popup', label: 'Popup Camper', image: 'assets/images/Popup_CNT.svg' },
];

/** Managing agency for boondocking sites. */
export type ListingAgency = 'BLM' | 'USFS' | 'NPS' | 'State Park' | 'Army Corps' | 'County' | 'Other';

/** Shared base for every map-pinnable stay. */
interface IListingBase {
  id: number;
  title: string;
  location: string;
  /** City/state, kept separate from `location` for building SEO slug routes
   *  (see `buildListingSlugSegments` in `listing-route.util.ts`). */
  city?: string;
  state?: string;
  coordinates: ILatLon;
  category: Category;
  /** Aligned to @curbnturf/objects' IListing/IBoondock.landTypes. Derived from
   * `category` via CATEGORY_LAND_TYPE where that mapping is unambiguous —
   * empty for `attraction`/`offgrid` listings (see CATEGORY_LAND_TYPE doc). */
  landTypes: ILandType['type'][];
  amenities: Amenity[];
  /** Guest-facing sub-detail per amenity, when the host recorded one — e.g.
   *  electricity → "30 / 50 amp", water → "Hookup at site". See `amenityDetailsFrom`. */
  amenityDetails?: Partial<Record<Amenity, string>>;
  image: string;
  /** Full ordered array of the listing's real uploaded photos, when known (populated for
   *  backend-sourced listings by `LiveSearchService`/`toPrivateListingFromEntity` etc.).
   *  Undefined/empty when no photos are known yet — `getListingDetail()` falls back to
   *  the single card `image` in that case. */
  images?: string[];
  /** YouTube video code for a property featured on the RVCast podcast, if any. */
  youtubeCode?: string;
  /** Link to the property's RVCast podcast episode page, if any. */
  rvcastUrl?: string;
}

/**
 * Private host stay — has a price, rating, reviews, and a Reserve/Instant Book path.
 * Genuinely separate from boondocking sites — this mirrors @curbnturf/objects, where
 * `IListing` and `IBoondock` are two distinct top-level entities, not variants of one
 * discriminated union. There is deliberately no `kind`/type-tag field here: code that
 * needs to know "is this a boondocking site" checks structurally (`'agency' in x`)
 * rather than relying on a stored discriminant that has to be kept in sync across two
 * collections.
 */
export interface IPrivateListing extends IListingBase {
  /** **What the guest pays per night**, fee included — the same basis as
   *  `ISearchableListing.price`, which the server computes with
   *  `Site.getPrice()` (host price grossed up by `getFee()`).
   *
   *  This was ambiguous and it caused a real defect: `LiveSearchService` filled
   *  it from the search projection (guest price) while
   *  `toPrivateListingFromEntity()` filled it from `site.price` (the HOST's
   *  take-home). Same field, two meanings depending on provenance — so a guest
   *  opening a listing saw the host's $75 until the calendar resolved and it
   *  jumped to $88.82. Both sources now produce the guest price; the host's own
   *  figure lives in `hostPrice`. */
  price: number;
  /** **What the host is paid per night** — `Site.price`, the number they type
   *  into the wizard, before the platform's fee is added on top. Populated only
   *  by `toPrivateListingFromEntity()`, because only an entity-backed listing
   *  knows it: the search projection carries the grossed-up figure and cannot
   *  be reversed reliably. Host surfaces (e.g. the calendar's "base $X/night"
   *  and its price-override placeholder) must read THIS, never `price`. */
  hostPrice?: number;
  rating: number;
  reviewCount: number;
  /** Aligned to @curbnturf/objects' IListing.reservationProcess — 'instant' confirms
   * automatically when dates are available, 'standard' requires host approval. */
  reservationProcess: 'standard' | 'instant';
  /** Real count of host-configured extras (`ISearchableListing.extrasCount`), populated
   * by `LiveSearchService`. Optional because some sources of `IPrivateListing` (e.g. a
   * freshly-drafted, not-yet-published listing) have no real backend count to report. */
  extrasCount?: number;
  /** Real backend "is this listing live/bookable" flag — false until enable() is called.
   * Optional because not every source of `IPrivateListing` has a real backend flag to
   * report; only `toPrivateListingFromEntity()` populates it. */
  available?: boolean;
  /** Epoch seconds the listing was first enabled (`IListing.publishDate`) — the anchor
   *  the server's 30-day photo grace period counts from (see
   *  `photo-grace-period.util.ts`). Optional for the same reason as `available`. */
  publishDate?: number;
  /** Real host-configured taxes applied to this listing (`IListing.taxes`), populated by
   *  `toPrivateListingFromEntity()` for the host tax-settings toggle grid. Undefined for
   *  any source of `IPrivateListing` that isn't backed by a real fetched listing. */
  taxes?: ITax[];
  /** Real longest rig this listing accepts, in feet (`ISearchableListing.maxLength` /
   *  `Site.maxLength`), populated by `LiveSearchService` and
   *  `toPrivateListingFromEntity()`. Undefined when no real figure is known — callers
   *  must then say nothing rather than guess, because "will my rig fit" is the whole
   *  question a guest is asking. `getListingDetail()`'s `siteSpecs.maxRigLength` is a
   *  flat 35 placeholder and is NOT a safe source for a fit verdict. */
  maxRigLength?: number;
  /** Real host-chosen cancellation policy (`IListing.cancellationPolicy`, mapped through
   *  `reverseCancellationPolicy()`), populated by `toPrivateListingFromEntity()`. Undefined
   *  for any source of `IPrivateListing` that isn't backed by a real fetched listing —
   *  callers must then show nothing rather than guess, because this tier is a promise
   *  about refunds. `getListingDetail()`'s generic 'moderate' is a rendering placeholder
   *  for the detail shape and is NOT a safe source for a card chip. */
  cancellationTier?: CancellationTier;
}

/**
 * Boondocking public-land entry — no price, no rating, no reservation flow.
 * Genuinely separate from `IPrivateListing` — see that interface's doc comment.
 */
export interface IBoondockingListing extends IListingBase {
  agency: ListingAgency;
}

/**
 * Presentation-only union for the handful of views that render both collections
 * together (search feed, map, wishlists). Not a domain type — code that already
 * knows which collection it's working with should use `IPrivateListing` or
 * `IBoondockingListing` directly instead of this. Narrow with `'agency' in x`.
 */
type IStay = IPrivateListing | IBoondockingListing;
/** @deprecated Use `IStay` — kept as an alias during the boondock/listing split so
 * imports across the app don't all need to change in the same pass. */
export type IListing = IStay;

export const AGENCY_META: Record<ListingAgency, { label: string; icon: string }> = {
  BLM: { label: 'Bureau of Land Management', icon: 'landscape' },
  USFS: { label: 'U.S. Forest Service', icon: 'park' },
  NPS: { label: 'National Park Service', icon: 'forest' },
  'State Park': { label: 'State Park', icon: 'park' },
  'Army Corps': { label: 'Army Corps of Engineers', icon: 'shield' },
  County: { label: 'County Park', icon: 'apartment' },
  Other: { label: 'Public land', icon: 'public' },
};

/** Fixed bounds for the search page's price filter slider. Not derived from any
 *  particular set of listings (real or otherwise) — a slider needs stable bounds
 *  that don't shift under the user as the live catalog changes. */
export const PRICE_RANGE = { min: 0, max: 500 };

// =============================================================================
// LISTING DETAIL — extended fields for /listing detail page
// =============================================================================

export interface IHost {
  name: string;
  initials: string;
  avatar: string; // image URL — photo of the host or a relevant scene
  joinedYear: number;
  bio: string;
  responseHours: number; // e.g., 2 = "typically responds within 2 hours"
}

export type CancellationTier = 'easy-goin' | 'moderate' | 'strict' | 'exclusive';

export const CANCELLATION_TIER_META: Record<CancellationTier, { label: string; summary: string; color: string }> = {
  'easy-goin': { label: "Easy Goin'", summary: 'Free cancellation up to 1 day before check-in.', color: '#295d42' },
  moderate: { label: 'Moderate', summary: 'Free cancellation up to 3 days before check-in.', color: '#3B7B7A' },
  strict: { label: 'Strict', summary: 'Half refund up to 7 days before check-in.', color: '#E8A23D' },
  exclusive: { label: 'Exclusive', summary: 'Non-refundable. No refunds for cancellations.', color: '#A541A1' },
};

export interface IReview {
  authorName: string;
  authorInitials: string;
  date: string; // "March 2026"
  rating: number; // 1-5
  text: string;
  /** Stable id for real user-submitted reviews — drives the host-respond button
   *  and the helpful-vote ledger. Undefined for seeded mock reviews. */
  bookingId?: string;
  /** Host's public reply, when present. */
  hostResponse?: { text: string; respondedAt: string };
  /** Optional photos the guest attached to their review. Data URLs or
   *  remote URLs — the card renders them as a small horizontal scroll-row. */
  photos?: string[];
}

export interface ISubScores {
  cleanliness: number;
  communication: number;
  hookups: number;
  location: number;
  value: number;
}

export type TrustBadge = 'land-insured' | 'superhost';
// export type TrustBadge = 'verified-host' | 'id-checked' | 'land-insured' | 'superhost';

export const TRUST_BADGE_META: Record<TrustBadge, { label: string; icon: string }> = {
  // 'verified-host': { label: 'Verified host',  icon: 'verified' },
  // 'id-checked':    { label: 'ID checked',     icon: 'badge' },
  'land-insured': { label: 'Land insured', icon: 'shield' },
  superhost: { label: 'Superhost', icon: 'workspace_premium' },
};

export type NearbyType = 'gas' | 'grocery' | 'dump' | 'attraction' | 'restaurant';

export const NEARBY_META: Record<NearbyType, { label: string; icon: string }> = {
  gas: { label: 'Gas station', icon: 'local_gas_station' },
  grocery: { label: 'Grocery', icon: 'shopping_cart' },
  dump: { label: 'Dump station', icon: DUMP_STATION_ICON },
  attraction: { label: 'Attraction', icon: 'attractions' },
  restaurant: { label: 'Restaurant', icon: 'restaurant' },
};

interface INearbyPoi {
  type: NearbyType;
  name: string;
  distance: string; // "0.8 mi"
}

export type PadType = 'gravel' | 'grass' | 'concrete' | 'dirt';
export type Leveling = 'level' | 'mostly-level' | 'needs-blocks';
export type SewerType = 'full-hookup' | 'dump' | 'none';
export type SlideoutClearance = 'tight' | 'moderate' | 'open';
export type HookupAmps = 15 | 30 | 50 | null;

export const PAD_TYPE_META: Record<PadType, { label: string }> = {
  gravel: { label: 'Gravel' },
  grass: { label: 'Grass' },
  concrete: { label: 'Concrete' },
  dirt: { label: 'Dirt' },
};

export const LEVELING_META: Record<Leveling, { label: string }> = {
  level: { label: 'Level' },
  'mostly-level': { label: 'Mostly level' },
  'needs-blocks': { label: 'Needs blocks' },
};

export const SEWER_META: Record<SewerType, { label: string }> = {
  'full-hookup': { label: 'Full hookup' },
  dump: { label: 'Dump station' },
  none: { label: 'None' },
};

export const CLEARANCE_META: Record<SlideoutClearance, { label: string }> = {
  tight: { label: 'Tight — measure first' },
  moderate: { label: 'Moderate' },
  open: { label: 'Open — slides clear' },
};

interface IListingFaq {
  q: string;
  a: string;
}

export interface IAddOn {
  id: string;
  label: string;
  description: string;
  icon: string; // material symbols name
  price: number;
  unit: 'per stay' | 'per night' | 'per person' | 'per unit';
  /** Optional square thumbnail data URL — host-uploaded; ~400px max. */
  photo?: string;
  /** R15/A — Optional crop + rotation metadata that pairs with `photo`.
   *  Uses the same IPhotoMeta shape as IDraftListing.photoMeta[]. */
  photoMeta?: import('./draft-listing.types').IPhotoMeta;
  /** Real backend `IListingExtra.id`, assigned once HostListingDraftService
   *  has synced this add-on to a real listing (see syncRealExtras). Guest
   *  bookings need this to reference the extra on a real reservation —
   *  undefined for mock-only listings or add-ons not yet synced. */
  realExtraId?: number;
  /** The server's `IListingExtra.count` — "the maximum number of the extra that
   *  can be purchased with the reservation". When it is greater than 1 the
   *  add-on is genuinely countable and the guest gets a quantity stepper, which
   *  is what answers "Extra Vehicles $1/stay — per vehicle or per stay?"
   *  (Batch A #6). Undefined means uncapped/not stated. */
  maxQuantity?: number;
}

interface ISiteSpecs {
  padType: PadType;
  padLength: number; // feet
  maxRigLength: number; // feet
  leveling: Leveling;
  hookupAmps: HookupAmps;
  waterAvailable: boolean;
  sewerType: SewerType;
  bigRigFriendly: boolean;
  slideoutClearance: SlideoutClearance;
}

export interface IListingDetail {
  description: string;
  host: IHost;
  photos: string[];
  /** Optional per-photo captions, index-aligned with `photos`. Empty/undefined for seeded listings. */
  photoCaptions?: string[];
  houseRules: string[];
  cancellationTier: CancellationTier;
  maxGuests: number; // e.g. 4 — total guests the site can accommodate
  maxStayNights: number; // e.g. 14 — longest reservation allowed
  subScores: ISubScores;
  reviews: IReview[];
  nearby: INearbyPoi[];
  trustBadges: TrustBadge[];
  /**
   * ISO YYYY-MM-DD strings — dates already booked / unavailable.
   *
   * **Optional on purpose (F12).** A `getListingDetail()` stub and a
   * `toListingDetailFromEntity()` result are the same type, so a consumer that
   * takes an `IListingDetail` type-checks against either. When the stub handed
   * back a confident `[]`, a caller reading it would have concluded every date
   * was free — the one placeholder in that family whose failure mode is a double
   * booking. `undefined` forces the caller to decide what absence means instead
   * of inheriting a wrong answer.
   *
   * Today the only reader, `BookingReviewComponent.isDateRangeAvailable()`, is on
   * the real path, so this is a guard against a future caller rather than a live
   * fix. Availability elsewhere comes from `ListingAvailabilityService`.
   */
  unavailableDates?: string[];
  siteSpecs: ISiteSpecs;
  addOns: IAddOn[];
  faqs: IListingFaq[];
}

/**
 * Returns the detail block for a listing that has no fetched entity in hand (e.g.
 * booking-review/confirm, listing cards, host-dashboard widgets). User-published
 * listings get their detail built from the publish-time snapshot (real host-entered
 * content — see `buildUserPublishedDetail`). Everything else gets an honest stub.
 * Fields with no backend source at all yet (`nearby`, `trustBadges`, `faqs`, most of
 * `siteSpecs`) are left empty/neutral rather than fabricated — each is marked below
 * for future backend work.
 *
 * Callers that DO have a fetched entity in hand (`listing-details.component.ts`'s
 * `applyListing()`) skip this function entirely and use `toListingDetailFromEntity()`
 * (`listing-entity.mapper.ts`) instead, which builds the same shape directly from real
 * data — `boondock-details.component.ts` still layers `boondockDetailOverridesFromEntity()`
 * on top of this stub, since boondocking has less to override.
 */
export function getListingDetail(listing: IListing): IListingDetail {
  // User-published listings get their detail rebuilt from the publish-time snapshot
  // so we render the host's actual photos, name, rules, etc.
  if (!('agency' in listing)) {
    const snap = readPublishedSnapshot(listing.id);
    if (snap) return buildUserPublishedDetail(listing, snap);
  }

  // Real listings carry their own uploaded photos in `images` (populated by
  // LiveSearchService/toPrivateListingFromEntity) — fall back to the single card
  // image only when nothing richer is known yet.
  const photos = listing.images && listing.images.length > 0 ? listing.images.slice(0, 5) : [listing.image];

  return {
    description: `${listing.title} — a CurbNTurf stay in ${listing.location}.`,
    host: {
      name: '',
      initials: '',
      avatar: listing.image,
      joinedYear: new Date().getFullYear(),
      bio: '',
      responseHours: 24,
    },
    photos,
    houseRules: [],
    cancellationTier: 'moderate', // TODO: placeholder until the caller has entity access to override with the real cancellationPolicy
    maxGuests: 4, // TODO: placeholder until the caller has entity access to override with real per-site capacity
    maxStayNights: 14, // TODO: placeholder until the caller has entity access to override with the real maxLengthOfStayInDays
    subScores: { cleanliness: 0, communication: 0, hookups: 0, location: 0, value: 0 },
    reviews: [],
    nearby: [], // TODO: no backend field yet (no nearby-POI concept on Listing/Boondock)
    trustBadges: [], // TODO: no backend field yet (no trust-badge concept on Listing/Boondock)
    // Deliberately absent, not empty (F12). An empty array is a confident claim
    // that nothing is booked; undefined says "this stub does not know", which is
    // the truth. See IListingDetail.unavailableDates.
    unavailableDates: undefined,
    siteSpecs: {
      padType: 'gravel', // TODO: no backend field yet
      padLength: 35, // TODO: no backend field yet
      maxRigLength: 35, // TODO: placeholder until the caller has entity access to override with the real Site.maxLength
      leveling: 'mostly-level', // TODO: no backend field yet
      hookupAmps: null, // TODO: no backend field yet
      waterAvailable: false, // TODO: no backend field yet
      sewerType: 'none', // TODO: no backend field yet
      bigRigFriendly: false, // TODO: no backend field yet
      slideoutClearance: 'moderate', // TODO: no backend field yet
    },
    addOns: [],
    faqs: [], // TODO: no backend field yet (no FAQ concept on Listing/Boondock)
  };
}

// ─────────────────────── User-published listing detail builders ───────────────────────

/**
 * Build an `IListingDetail` for a user-published listing from its saved snapshot.
 * Reflects only what the host actually entered — no mock host, no stock photos,
 * no fabricated add-ons / reviews / trust badges.
 */
function buildUserPublishedDetail(listing: IListing, snap: IPublishedSnapshot): IListingDetail {
  const d = snap.draft;
  return {
    description: d.description || `${listing.title} — hosted on CurbNTurf.`,
    host: {
      name: snap.hostName,
      initials: snap.hostInitials,
      avatar: snap.hostAvatar,
      joinedYear: snap.hostJoinedYear,
      bio: '',
      responseHours: 24,
    },
    photos: d.photos && d.photos.length > 0 ? d.photos : [listing.image],
    photoCaptions: d.photoCaptions ?? [],
    houseRules: houseRulesFromDraft(d.rules, d.customRules ?? ''),
    cancellationTier: d.cancellationTier ?? 'moderate',
    maxGuests: d.guestCapacity ?? 2,
    maxStayNights: d.maxNights ?? 14,
    subScores: { cleanliness: 0, communication: 0, hookups: 0, location: 0, value: 0 },
    reviews: [],
    nearby: [], // TODO: no backend field yet (no nearby-POI concept)
    trustBadges: [], // new host — no badges yet
    unavailableDates: [],
    siteSpecs: siteSpecsFromDraft(d),
    // Filter incomplete rows (no name / negative price) so half-edited add-ons
    // never reach guests. Host's working copy is preserved in the draft.
    addOns: (d.addOns ?? []).filter((a) => a.label?.trim().length >= 2 && (a.price ?? 0) >= 0),
    faqs: [], // wizard doesn't collect FAQs (yet)
  };
}

/** Convert the wizard's IHouseRules booleans + custom textarea into the string[] the detail uses. */
function houseRulesFromDraft(rules: IHouseRules | undefined, customRules: string): string[] {
  const out: string[] = [];
  if (rules?.noSmoking) out.push('No smoking on the property');
  if (rules?.noParties) out.push('No parties or events');
  if (rules?.quietHours) out.push('Quiet hours 10 PM – 7 AM');
  if (rules?.noFireworks) out.push('No fireworks on the premises');
  if (rules?.noFirearms) out.push('No firearms on the premises');
  for (const line of (customRules || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)) {
    out.push(line);
  }
  return out;
}

/** Derive an ISiteSpecs from the wizard's max-rig + amenities. */
function siteSpecsFromDraft(d: IDraftListing): ISiteSpecs {
  const amenities = d.amenities ?? [];
  const hasElectricity = amenities.includes('electricity');
  const hasWater = amenities.includes('water');
  const hasSewage = amenities.includes('sewage');
  const hasDump = amenities.includes('dump');
  const maxRigLength = d.maxRig?.length && d.maxRig.length > 0 ? d.maxRig.length : 35;
  const sewerType: SewerType = hasSewage ? 'full-hookup' : hasDump ? 'dump' : 'none';
  return {
    padType: 'gravel', // sensible default; future: ask in wizard
    padLength: maxRigLength,
    maxRigLength,
    leveling: 'mostly-level',
    hookupAmps: hasElectricity ? 30 : null,
    waterAvailable: hasWater,
    sewerType,
    bigRigFriendly: maxRigLength >= 38,
    slideoutClearance: 'moderate',
  };
}
